import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  computed,
  inject,
  input,
  output,
  viewChild
} from '@angular/core';
import { ExpandableComponent } from '../../../shared/expandable-component/expandable.component';
import { IconComponent } from '../../../shared/icons/icon.component';
import { IconNameType } from '../../../shared/icons/icon-name-type.enum';
import { CommonModule } from '@angular/common';
import { IngredientComponent } from '../ingredient/ingredient.component';
import { IngredientChoice } from '../../models/menu/ingredient-choice.model';
import { OrderedChoice } from '../../models/ordering/ordered-choice.model';
import { IngredientGroupComponent } from '../ingredient-group/ingredient-group.component';
import { OrderedChoiceService } from '../../services/ordering/ordered-choice.service';
import { OrderedItem } from '../../models/ordering/ordered-item.model';
import { ChoiceValidationResult } from '../../services/ordering/choice-validation-result.model';
import { OrderableItem } from '../../models/menu/orderable-item.model';
import { OrderableIngredient } from '../../models/menu/orderable-ingredient.model';
import { TranslocoPipe } from '@ngneat/transloco';
import { AuditService } from '../../services/audit.service';
@Component({
  selector: 'cwo-choice',
  standalone: true,
  imports: [
    ExpandableComponent,
    IconComponent,
    CommonModule,
    IngredientComponent,
    IconComponent,
    IngredientGroupComponent,
    TranslocoPipe
  ],
  templateUrl: './choice.component.html',
  styleUrl: './choice.component.scss',
})
export class ChoiceComponent implements OnChanges, AfterViewInit, OnDestroy {
  private cd = inject(ChangeDetectorRef);
  orderedChoiceService = inject(OrderedChoiceService);
  private auditService = inject(AuditService);
  choice = input.required<IngredientChoice>();
  name = input<string | undefined>();
  iconNameType = IconNameType;
  orderedChoice = input.required<OrderedChoice>();

  orderedItem = input.required<OrderedItem>();
  orderableItem = input.required<OrderableItem>();
  
  firstInvalidChoice = input('');
  isSpecial = input.required<boolean>();
  isInChoiceGroup = input<boolean>(false);

  dependableChoices = input<Map<string, IngredientChoice[]>>(new Map<string, IngredientChoice[]>());

  toggleWarningsAfterSubmission = input(false);
  // editedAndFullfieldAfterSubmission = false;
  bouncingTextToggle = false;
  choiceExpandableId = computed(
    () => `${this.choice().name}-expandable-section`
  );

  filteredIngredientsByGroup = computed(() => {
    const groups = this.choice().groups;
    const ingredients = this.choice().ingredients;
  
    return groups.map(group => ({
      ...group,
      filteredIngredients: ingredients.filter(ingredient => 
        group.ingredients.includes(ingredient.name)
      )
    }));
  });

  warningPillType?: 'neutural' | 'error' | 'complete';
  choiceValidator?: ChoiceValidationResult;

  expanded = false;

  overflowVisible = false;
  private static readonly EXPAND_DURATION_MS = 300;
  private overflowTimeout?: ReturnType<typeof setTimeout>;
  scrollMarginTop?: number;
  scrollMarginTopWarning?: number;

  readonly warningMessageRef = viewChild<ElementRef>('warningMessageElement');
  ingredientTooltipVisible: string = '';

  expandedInput = input<boolean>(false);

  readonly resetWarningsAfterSubmissionValue = output();

  readonly updateSpecialItem = output<OrderedItem>();

  private elRef = inject(ElementRef);

  constructor() {}

  calcualteScrollMarginTop() {
    const informationContainerElRef = document.getElementById(
      'item-editor-item-information-container'
    );
    this.scrollMarginTop = informationContainerElRef?.getBoundingClientRect()
      .height
      ? informationContainerElRef?.getBoundingClientRect().height + 10
      : undefined;

    this.scrollMarginTopWarning = this.scrollMarginTop
      ? this.scrollMarginTop + 22
      : undefined; //22px of the header
  }

  onIngredientAdded(ingredient: OrderableIngredient) {
  
    if(ingredient.isExtraIngredientUpsell && !this.orderableItem().isExtraIngredientTooltipOpenedOnce ) {
      this.ingredientTooltipVisible = ingredient.name;
    }

  }

  ngAfterViewInit(): void {
    this.checkIfChoiceIsValid(true);
    
    if(this.expandedInput()){
      this.expanded = this.expandedInput();
    }
    this.updateOverflowVisibility(this.expanded, false);
    this.cd.detectChanges();
  }

  checkIfChoiceIsValid(controlExpansion = false) {
    this.choiceValidator = this.orderedChoiceService.validateChoice(
      this.orderedChoice(),
      this.choice()
    );
    if (this.choiceValidator) {
      this.orderedChoice().warnings = this.choiceValidator?.valid
        ? []
        : [this.choiceValidator?.longMessage];

    if(controlExpansion){
      this.expanded =
        this.choice().isOpenOnWeb ||
        (this.choiceValidator && !this.choiceValidator.valid);
    }
  }
}
  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['toggleWarningsAfterSubmission'] ||
      changes['firstInvalidChoice']
    ) {
      if (
        this.toggleWarningsAfterSubmission() &&
        this.firstInvalidChoice() === this.choice().name
      ) {
        this.calcualteScrollMarginTop();
        this.elRef.nativeElement.setAttribute(
          'style',
          `scroll-margin-top: ${this.scrollMarginTopWarning}px`
        );
        this.elRef?.nativeElement.scrollIntoView({ behavior: 'smooth' });
      }
      
    }
    if(changes['orderedChoice'] && this.choice().ingredients.length > 0) {
      this.checkIfChoiceIsValid();
    }
  }

  handleExpansion(expanded: boolean) {
    this.auditService.createAudit(() => `${expanded ? 'Expanded' : 'Collapsed'} ${this.choice().name} choice on ${this.orderedItem().item} item`);
    this.calcualteScrollMarginTop();
    this.expanded = expanded
    this.updateOverflowVisibility(expanded, true);
    if (this.expanded) {
      const modalContent = document.getElementById(
        'item-editor-scrollable-wrapper'
      );
      const el = document.getElementById(this.choiceExpandableId());
      const elContent = document.getElementById(
        this.choiceExpandableId() + 'content'
      );
      const footer = document.getElementById('item-editor-footer');

      if (el && elContent && modalContent && footer) {
        const modalContentRect = modalContent.getBoundingClientRect();
        const elContentRect = elContent.getBoundingClientRect();
        const footerRect = footer.getBoundingClientRect();

        // Calculate the visible area within the modal content
        const visibleAreaHeight = modalContentRect.height - footerRect.height;

        // Check if the content's bottom position exceeds the visible area
        if (elContentRect.bottom > visibleAreaHeight) {
          // Wait for the category to be fully expanded before scrolling
          setTimeout(() => {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 10);
        }
      }
    }
  }

  updateOverflowVisibility(expanded: boolean, animate: boolean) {
    clearTimeout(this.overflowTimeout);
    if (!expanded) {
      this.overflowVisible = false;
      return;
    }
    if (!animate) {
      this.overflowVisible = true;
      return;
    }
    this.overflowTimeout = setTimeout(() => {
      this.overflowVisible = true;
      this.cd.detectChanges();
    }, ChoiceComponent.EXPAND_DURATION_MS);
  }

  ngOnDestroy(): void {
    clearTimeout(this.overflowTimeout);
  }

  setWarningMessage(choiceValidation: ChoiceValidationResult) {
    this.resetWarningsAfterSubmissionValue.emit();
    if (!choiceValidation.valid) {
      if(!this.choiceValidator?.valid){
         this.auditService.createAudit(() => `New ingridient clicked from  ${this.choice().name} but Warning : ${choiceValidation.longMessage}`)
        return;
      }
      this.orderedChoice().warnings = [choiceValidation.longMessage];
    } else {
      this.orderedChoice().warnings = [];
    }
    const invalidForFirstTime = choiceValidation.longMessage !== this.choiceValidator?.longMessage;
    this.choiceValidator = choiceValidation;

    if(invalidForFirstTime && choiceValidation.longMessage) {
      this.warningMessageRef()?.nativeElement.scrollIntoView({
        behavior: 'smooth',
      });
      this.bouncingTextToggle = true;
        this.auditService.createAudit(() => `New ingridient clicked from  ${this.choice().name} but Warning : ${choiceValidation.longMessage}`)
      //reseting bouncingtexttoggle after animation is done
      setTimeout(() => {
        this.bouncingTextToggle = false;
      }, 1500);
    }
  }
}
